/* Kiti: collection filter redesign (feature/collection-filter)
 * - Chip links load through the theme's AJAX filtering when it is enabled.
 * - Floating Filter pill (mobile/tablet) shows once the chip row has scrolled
 *   out of view, and hides again when the footer comes into view.
 */
(() => {
  /* ------------------------------------------------------------------
   * Grouped size picker
   * Buttons carry data-kiti-size-param and data-kiti-size-values
   * ("S|XS/S|S/M"). In the sidebar/drawer they tick the theme's own
   * checkboxes straight away; in the size sheet they are staged and
   * applied together on "Show".
   * ------------------------------------------------------------------ */
  const getForm = () => document.getElementById('CollectionFilterForm');
  const valuesOf = (btn) => (btn.dataset.kitiSizeValues || '').split('|').filter(Boolean);
  const checkboxesFor = (form, param) => Array.from(form.querySelectorAll('input[type="checkbox"]'))
    .filter((input) => input.name === param);

  const setPressed = (btn, pressed) => {
    btn.classList.toggle('is-selected', pressed);
    btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
  };

  // Tick/untick checkboxes, then fire change events so the theme's AJAX
  // filtering and "auto apply hide out of stock" both run as normal
  const applyToForm = (form, param, values, checked) => {
    const changed = [];
    checkboxesFor(form, param).forEach((input) => {
      if (values.includes(input.value) && !input.disabled && input.checked !== checked) {
        input.checked = checked;
        changed.push(input);
      }
    });
    return changed;
  };
  const fireChanges = (inputs) => {
    inputs.forEach((input) => input.dispatchEvent(new Event('change', { bubbles: true })));
  };

  const sheetSelection = (sheet) => {
    const byParam = {};
    sheet.querySelectorAll('.kiti-size').forEach((btn) => {
      const param = btn.dataset.kitiSizeParam;
      if (!byParam[param]) byParam[param] = new Set();
      if (btn.classList.contains('is-selected')) {
        valuesOf(btn).forEach((v) => byParam[param].add(v));
      }
    });
    return byParam;
  };

  let countTimer = null;
  let countController = null;
  const updateSheetCount = (sheet) => {
    const form = getForm();
    const container = document.querySelector('filter-container');
    const chips = document.querySelector('[data-kiti-chips]');
    const countEl = sheet.querySelector('[data-kiti-size-count]');
    if (!form || !container || !countEl) return;

    clearTimeout(countTimer);
    countTimer = setTimeout(() => {
      const selection = sheetSelection(sheet);
      const params = new URLSearchParams();
      new FormData(form).forEach((val, key) => {
        if (!selection[key] && val !== '') params.append(key, val);
      });
      let anySelected = false;
      Object.keys(selection).forEach((key) => {
        selection[key].forEach((v) => { params.append(key, v); anySelected = true; });
      });
      const filters = container.querySelector('.filters');
      if (anySelected && filters && filters.dataset.autoApplyHideUnavailable === 'true' && !params.get('filter.v.availability')) {
        params.set('filter.v.availability', '1');
      }
      params.set('section_id', container.dataset.filterSectionId);

      if (countController) countController.abort();
      countController = new AbortController();
      sheet.classList.add('is-counting');
      fetch(`${window.location.pathname}?${params.toString()}`, { signal: countController.signal })
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const el = doc.querySelector('[data-kiti-total]');
          if (el) countEl.textContent = el.dataset.kitiTotal;
        })
        .catch(() => {})
        .finally(() => sheet.classList.remove('is-counting'));
    }, 250);
  };

  const openSheet = () => {
    const sheet = document.querySelector('[data-kiti-size-sheet]');
    if (!sheet) return;
    sheet.hidden = false;
    document.body.classList.add('kiti-size-sheet-open');
    const close = sheet.querySelector('.kiti-size-sheet__close');
    if (close) close.focus();
  };
  const closeSheet = () => {
    const sheet = document.querySelector('[data-kiti-size-sheet]');
    if (!sheet) return;
    sheet.hidden = true;
    document.body.classList.remove('kiti-size-sheet-open');
    const chip = document.querySelector('[data-kiti-size-open]');
    if (chip) chip.focus();
  };

  const initSizeGroups = () => {
    document.addEventListener('click', (evt) => {
      // Department chips / "All clothing" buttons: set a filter's values within a scope in one go
      const setter = evt.target.closest('[data-kiti-set-param]');
      if (setter) {
        evt.preventDefault();
        const form = getForm();
        if (!form) return;
        const param = setter.dataset.kitiSetParam;
        const scope = (setter.dataset.kitiScopeValues || '').split('|').filter(Boolean);
        const target = new Set((setter.dataset.kitiSetValues || '').split('|').filter(Boolean));
        const changed = [];
        checkboxesFor(form, param).forEach((input) => {
          if (!scope.includes(input.value)) return;
          const want = target.has(input.value);
          if (input.checked !== want && (!input.disabled || !want)) {
            input.checked = want;
            changed.push(input);
          }
        });
        setter.classList.add('is-loading');
        fireChanges(changed);
        return;
      }

      const sizeTab = evt.target.closest('[data-kiti-size-tab]');
      if (sizeTab) {
        evt.preventDefault();
        const groups = sizeTab.closest('[data-kiti-size-groups]');
        const key = sizeTab.dataset.kitiSizeTab;
        groups.querySelectorAll('[data-kiti-size-tab]').forEach((tab) => {
          const current = tab === sizeTab;
          tab.classList.toggle('is-current', current);
          tab.setAttribute('aria-selected', current ? 'true' : 'false');
        });
        groups.querySelectorAll('[data-kiti-size-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.kitiSizePanel !== key;
        });
        return;
      }

      const typeMore = evt.target.closest('[data-kiti-type-more]');
      if (typeMore) {
        evt.preventDefault();
        const list = typeMore.parentElement;
        if (list) {
          const expanded = list.classList.toggle('kiti-list-expanded');
          typeMore.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        }
        return;
      }

      if (evt.target.closest('[data-kiti-size-open]')) {
        evt.preventDefault();
        openSheet();
        return;
      }
      if (evt.target.closest('[data-kiti-size-close]')) {
        evt.preventDefault();
        closeSheet();
        return;
      }

      const sheet = evt.target.closest('[data-kiti-size-sheet]');

      if (sheet && evt.target.closest('[data-kiti-size-clear]')) {
        sheet.querySelectorAll('.kiti-size.is-selected').forEach((btn) => setPressed(btn, false));
        updateSheetCount(sheet);
        return;
      }

      if (sheet && evt.target.closest('[data-kiti-size-apply]')) {
        const form = getForm();
        if (form) {
          const selection = sheetSelection(sheet);
          const changed = [];
          Object.keys(selection).forEach((param) => {
            checkboxesFor(form, param).forEach((input) => {
              const want = selection[param].has(input.value);
              if (input.checked !== want && (!input.disabled || !want)) {
                input.checked = want;
                changed.push(input);
              }
            });
          });
          fireChanges(changed);
        }
        closeSheet();
        return;
      }

      const btn = evt.target.closest('.kiti-size');
      if (!btn || btn.disabled) return;
      const groups = btn.closest('[data-kiti-size-groups]');
      if (!groups) return;
      evt.preventDefault();
      const pressed = !btn.classList.contains('is-selected');

      if (groups.dataset.kitiSizeGroups === 'sheet') {
        setPressed(btn, pressed);
        updateSheetCount(btn.closest('[data-kiti-size-sheet]'));
        return;
      }

      // Sidebar / drawer: apply straight away
      const form = getForm();
      if (!form) return;
      setPressed(btn, pressed);
      fireChanges(applyToForm(form, btn.dataset.kitiSizeParam, valuesOf(btn), pressed));
    });

    document.addEventListener('keydown', (evt) => {
      if (evt.key === 'Escape' && document.body.classList.contains('kiti-size-sheet-open')) {
        closeSheet();
      }
    });
  };

  const init = () => {
    initSizeGroups();

    const chips = document.querySelector('[data-kiti-chips]');
    if (!chips) return;

    const body = document.body;
    let chipsGone = false;
    let footerInView = false;
    const update = () => body.classList.toggle('kiti-pill-visible', chipsGone && !footerInView);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        chipsGone = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        update();
      }).observe(chips);

      const footer = document.querySelector('.section-footer, footer');
      if (footer) {
        new IntersectionObserver(([entry]) => {
          footerInView = entry.isIntersecting;
          update();
        }).observe(footer);
      }
    }

    // "+ N more" chip reveals the rest of the chips (and "Show fewer" hides them again)
    document.addEventListener('click', (evt) => {
      const more = evt.target.closest('[data-kiti-chips-more]');
      if (!more) return;
      const expanded = chips.classList.toggle('kiti-chips--expanded');
      more.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });

    // Chip links: use the theme's AJAX loader so the grid, sidebar and chips refresh in place
    document.addEventListener('click', (evt) => {
      const link = evt.target.closest('[data-kiti-chip-link]');
      if (!link) return;
      const container = document.querySelector('filter-container');
      if (container && container.dataset.ajaxFiltering === 'true' && typeof container.ajaxLoadUrl === 'function') {
        evt.preventDefault();
        container.ajaxLoadUrl(link.getAttribute('href'));
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
